import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { DeseretBook } from '../../../dbcom/api'
import { FullError } from '../../../errors'
import { Logger, formatErrorMessage } from '../../../dbcom'
import { helpers } from '../../../helpers'

const logger = new Logger({ context: 'product/video/VideoTemplate' })

/**
 * React hook for building info/help text for deseret video context
 *
 * @returns {object} Contains the info text, is we should show the info icon, helpText and a link if needed.
 */
export const useActionContext = ({
  user = {},
  hasDeseretVideo = false,
  hasVip = false,
  isFree = false,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const deseretVideoSubLink = '/subscriptions/video'
  const userPaymentPage = '/account/subscriptions'

  // Info text above the play button
  const [infoText, setInfoText] = useState('')
  const [showInfoIcon, setShowInfoIcon] = useState(false)

  // Settings for text below the play button
  const [helpText, setHelpText] = useState('')
  const [helpTextLink, setHelpTextLink] = useState('')

  const userStatus = window.__USER__ || {}

  /**
   * Obtains the Deseret Video monthly subscription
   *
   * @returns {object} Deseret Video monthly subscription
   */
  const getDeseretVideoSubscription = async () => {
    try {
      // Obtain user payment sources
      const response = await DeseretBook.subscriptionPlan().all()

      // Check for errors within the response
      if (response.error || response.errors) {
        throw new FullError(response)
      }

      const videoMonthlySub =
        (await response) &&
        response.subscriptionPlans.filter(
          (subscription) =>
            subscription.type === 'SubscriptionPlan::DeseretVideo' && subscription.monthly,
        )[0]

      return await videoMonthlySub
    } catch (error) {
      const { errors, message } = error

      logger.error(`Unable to get subscription details. "${formatErrorMessage(message, errors)}"`, {
        errors,
      })

      return null
    }
  }

  /**
   * Returns whether a subscription is active or not.
   * It tests by the serviceableUntil date of the subs status
   *
   * @param {object} sub - The subscription to test
   *
   * @returns {bool} whether the passed sub is active
   */
  const isSubActive = (sub = {}) => {
    const currentDate = new Date().toISOString().slice(0, 10)

    return sub.serviceableUntil >= currentDate || sub.status === 'Active'
  }

  // Grabs the first Deseret Video sub for the user. It can be active or inactive.
  const userVideoSub =
    user &&
    user.subscriptions
      .filter(
        ({ subscriptionPlan, awaitingCompletion }) =>
          !awaitingCompletion &&
          subscriptionPlan &&
          subscriptionPlan.type === 'SubscriptionPlan::DeseretVideo',
      )
      .sort((subA, subB) => isSubActive(subB) - isSubActive(subA))[0]
  // Looks at the window.__USER__ object from _gtm.html.erb
  const isTrial = userVideoSub && userVideoSub.trialPeriod && hasDeseretVideo

  // The user has had a video sub, but it is not active.
  const isInactive = userVideoSub && !hasDeseretVideo

  /**
   * Obtains the users card for the current subscription.
   * Then calculates then return the number of days til the cards expiration date
   *
   * @returns {number} Number of days till expiration
   */
  const subCardExpiration = async () => {
    try {
      // Obtain user payment sources
      const paymentSources = await DeseretBook.user(user)
        .walletPaymentSource()
        .all({ perPage: 1000 })

      // Check for errors within the response
      if (paymentSources.error || paymentSources.errors) {
        throw new FullError(paymentSources)
      }

      // Locate the users payment source that matches the one on the subscription
      const cardForSub =
        paymentSources &&
        paymentSources.walletPaymentSources.filter(
          (walletPaymentSource) =>
            walletPaymentSource.paymentSource &&
            walletPaymentSource.paymentSource.id === userVideoSub.creditCardId,
        )[0]

      const cardInfo = cardForSub && cardForSub.paymentSource

      return cardInfo && helpers.calcDaysFromNow(new Date(cardInfo.year, cardInfo.month, 0))
    } catch (error) {
      const { errors, message } = error

      logger.error(`Unable to get user payment sources "${formatErrorMessage(message, errors)}"`, {
        errors,
      })

      return null
    }
  }

  /**
   * Decides which text should be displayed by using the users status and subscription for Free products
   *
   * @returns {object} Object containing info and action text to be displayed above and below the action button
   */
  const freeText = () => {
    let newInfoText = ''
    let newHelpText = ''
    let newShowInfoIcon = false
    let actionLink = ''
    const cardExpirationDate = subCardExpiration()

    // When the user is not logged in, does not have a sub, or the sub is not active
    if (!userStatus.loggedIn || !hasDeseretVideo || !hasVip) {
      newInfoText = 'Stream for free. This unlocked Deseret Video+ title is free to watch.'
      newHelpText = 'Access more videos with Deseret Video+.'
      actionLink = deseretVideoSubLink

      // When the sub is a trial
    } else if (isTrial) {
      newInfoText = `Your Deseret Video+ trial ends in ${helpers.calcDaysFromNow(
        new Date(userVideoSub.nextBillingDate),
      )} days.`
      newHelpText =
        'Sign up to continue watching hundreds of hours of movies, courses, talks, and lifestyle videos with Deseret Video+.'
      newShowInfoIcon = true

      // When the sub is active but the card expires soon
    } else if (cardExpirationDate < 30) {
      newInfoText = `Your Deseret Video+ access ends in ${cardExpirationDate} days.`
      newHelpText = 'Update your payment to continue access.'
      newShowInfoIcon = true
      actionLink = userPaymentPage

      // When the sub is active
    } else if (hasDeseretVideo) {
      newInfoText = 'You have access with Deseret Video+.'
    }

    return { newInfoText, newHelpText, newShowInfoIcon, actionLink }
  }

  /**
   * Decides which text should be displayed by using the users status and subscription for Premium products
   *
   * @returns {object} Object containing info and action text to be displayed above and below the action button
   */
  const premiumText = async () => {
    let newInfoText = ''
    let newHelpText = ''
    let newShowInfoIcon = false
    let actionLink = ''
    const cardExpirationDate = await subCardExpiration()
    const deseretVideoSub = await getDeseretVideoSubscription()

    // When the user does not have a Deseret Video sub.

    if (!userVideoSub && !hasVip) {
      newInfoText = `Stream hundreds of hours of movies, courses, talks, and lifestyle videos with Deseret Video+${
        deseretVideoSub ? ` starting at $${deseretVideoSub.price}/month` : ''
      }.`
      newHelpText = 'New subscribers only.'

      // When the sub is inactive
    } else if (isInactive) {
      newInfoText = `Stream hundreds of hours of movies, courses, talks, and lifestyle videos with Deseret Video+${
        deseretVideoSub ? ` starting at $${deseretVideoSub.price}/month` : ''
      }.`
      newHelpText = 'Access more videos with Deseret Video+.'
      actionLink = deseretVideoSubLink

      // When the sub is a trial
    } else if (isTrial) {
      newInfoText = `Your Deseret Video+ trial ends in ${helpers.calcDaysFromNow(
        new Date(userVideoSub.nextBillingDate),
      )} days.`
      newHelpText =
        'Sign up to continue watching hundreds of hours of movies, courses, talks, and lifestyle videos with Deseret Video+.'
      newShowInfoIcon = true
      actionLink = deseretVideoSubLink

      // When the card expires this month
    } else if (cardExpirationDate < 30) {
      newInfoText = `Your Deseret Video+ access ends in ${cardExpirationDate} days.`
      newHelpText = 'Update your payment to continue access.'
      newShowInfoIcon = true
      actionLink = userPaymentPage

      // When the sub is active
    } else if (hasVip) {
      // newInfoText = 'Info Text'
      newHelpText = 'You have access to Deseret Video+.'
    } else if (hasDeseretVideo) {
      newInfoText = 'You have access with Deseret Video+.'
    } else {
      newInfoText = `Stream hundreds of hours of movies, courses, talks, and lifestyle videos with Deseret Video+${
        deseretVideoSub ? ` starting at $${deseretVideoSub.price}/month` : ''
      }.`
      newHelpText = 'New subscribers only.'
    }

    return { isLoading, newInfoText, newHelpText, newShowInfoIcon, actionLink }
  }

  /**
   * Decides whether to get display text for a free/premium product
   * then updates them in state
   */
  const initialize = async () => {
    const selectedTextOptions = isFree ? await freeText() : await premiumText()

    setInfoText(selectedTextOptions.newInfoText)
    setShowInfoIcon(selectedTextOptions.newShowInfoIcon)

    setHelpText(selectedTextOptions.newHelpText)
    setHelpTextLink(selectedTextOptions.actionLink)
    setIsLoading(false)
  }

  useEffect(() => {
    initialize()
  }, [hasDeseretVideo, hasVip])

  return { isLoading, infoText, showInfoIcon, helpText, helpTextLink, userVideoSub }
}

useActionContext.propTypes = {
  user: PropTypes.shape(PropTypes.any),
  hasDeseretVideo: PropTypes.bool,
  hasVip: PropTypes.bool,
  isFree: PropTypes.bool,
}
